import type { FC } from "react";
import { Link } from "react-router";
import { ConfirmRow } from "../../../../components/return-form/ConfirmRow";
import {
  describeReturnRequestCompletion,
  describeReturnRequestCompletionNote,
  RETURN_REQUEST_STATUS_LABEL_JA,
} from "../../../../libs/models/returns/customerLabels";
import { normalizeOrderCode } from "../../../../libs/mypage/orderCode";
import { Container } from "../../components/Container";
import type { OrderDetailDTO } from "../../components/format";
import { LinkButton } from "../../components/LinkButton";
import { PageHeading } from "../../components/PageHeading";
import { BackLink } from "./BackLink";

/**
 * 返品申請ページで、その注文に既に申請があるときに申請フォームの代わりに出す画面。
 * 進行中なら「進んでいます」、返金 / 交換まで終わっていれば「完了しています」。
 * 見出しに「受付中」のような受付を表す言い方は使わない (これから申請できると読めるため)。
 */
export const ActiveReturnView: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const slug = normalizeOrderCode(order.code);
  const active = order.activeReturnRequest;
  if (!active) return null;
  const completed = active.phase === "completed";
  const completionNote = completed ? describeReturnRequestCompletionNote(active) : null;
  return (
    <Container>
      <BackLink slug={slug} />
      <PageHeading
        title={completed ? "返品のお手続きは完了しています" : "返品のお手続きが進んでいます"}
        description={
          // 「すべて完了」とは書かない。交換は手配までしか終わっておらず、事務手数料が
          // 未払いのまま残ることもある。何が終わったかはステータス行が具体的に示す。
          completed
            ? `ご注文 ${order.code} の返品について、下記のとおりお手続きが完了しています。`
            : `ご注文 ${order.code} について、返品申請をすでにお受けしています。`
        }
      />
      <dl className="divide-y divide-neutral-200 text-sm">
        {active.code && <ConfirmRow label="申請番号" value={active.code} />}
        <ConfirmRow
          label="ステータス"
          value={
            completed
              ? describeReturnRequestCompletion(active)
              : RETURN_REQUEST_STATUS_LABEL_JA[active.status]
          }
        />
      </dl>
      {completionNote && (
        <p className="pt-4 text-neutral-500 text-xs leading-relaxed">{completionNote}</p>
      )}
      {active.customerPageUrl && (
        <div className="pt-6">
          <LinkButton href={active.customerPageUrl} target="_blank" rel="noreferrer">
            申請の詳細を確認する
          </LinkButton>
        </div>
      )}
      <p className="mt-6 text-neutral-500 text-xs leading-relaxed">
        ご質問は{" "}
        <Link to="/contact" className="underline underline-offset-4 hover:text-neutral-900">
          お問い合わせ先
        </Link>{" "}
        よりご連絡ください。
      </p>
    </Container>
  );
};
